import React, { createContext, useContext, useState, useEffect } from 'react';

export type ConsentStatus = 'unknown' | 'accepted' | 'rejected';

interface CookieConsentContextType {
    consentStatus: ConsentStatus;
    setConsentStatus: (status: ConsentStatus) => void;
    acceptCookies: () => void;
    rejectCookies: () => void;
    resetConsent: () => void;
    isModalOpen: boolean;
    openSettings: () => void;
    closeSettings: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [consentStatus, setConsentStatus] = useState<ConsentStatus>('unknown');
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const storedConsent = localStorage.getItem('cookie_consent');
        if (storedConsent === 'accepted' || storedConsent === 'rejected') {
            setConsentStatus(storedConsent);
        }
    }, []);

    const acceptCookies = () => {
        localStorage.setItem('cookie_consent', 'accepted');
        setConsentStatus('accepted');
    };

    const rejectCookies = () => {
        localStorage.setItem('cookie_consent', 'rejected');
        setConsentStatus('rejected');
    };

    const resetConsent = () => {
        localStorage.removeItem('cookie_consent');
        setConsentStatus('unknown');
    };

    const openSettings = () => setIsModalOpen(true);
    const closeSettings = () => setIsModalOpen(false);

    return (
        <CookieConsentContext.Provider
            value={{
                consentStatus,
                setConsentStatus,
                acceptCookies,
                rejectCookies,
                resetConsent,
                isModalOpen,
                openSettings,
                closeSettings,
            }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (context === undefined) {
        throw new Error('useCookieConsent must be used within a CookieConsentProvider');
    }
    return context;
};
